import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeIcon from '@mui/icons-material/Merge';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, ButtonGroup, Fab, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { JSONPath } from 'jsonpath-plus';
import { createRef, useEffect, useState } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';
import { buildArraySchemaWarningPath, buildFieldPaths, getFilesWithUnConsolidatedExtractions, getFormatFunction, getJSONPathCompatibleKey, getParseFunction, getUserLocale, hasConsolidatedExtractions, renderTreatmentDates } from './PathUtils.js';

export default function MedicalExpenses({ caseInfo, setCaseInfo, fetchCaseInfoAndUpdateUI, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    const summaryTableRef = createRef();
    const hasConsolidated = hasConsolidatedExtractions(caseInfo, 'medical_records', 'medical_reports_canonicalize');
    const [showConsolidatedOnly, setShowConsolidatedOnly] = useState(hasConsolidated);
    const unconsolidatedFiles = getFilesWithUnConsolidatedExtractions(caseInfo, ['medical_records', 'personal_injury_catchall'], ['medical_expenses'], 'medical_reports_canonicalize');
    const hasUnconsolidated = unconsolidatedFiles.length > 0;
    const isMedicalRecordsInProgress = caseInfo && caseInfo['documents'] && Object.values(caseInfo['documents']).some(doc => doc['analysis_queue']?.includes('categorize') || doc['analysis_queue']?.includes('medical_records') || doc['analysis_queue']?.includes('personal_injury_catchall'));
    const isConsolidationInProgress = caseInfo['analysis_queue']?.[0]?.includes('medical_reports_');
    const isProcessingInProgress = caseInfo['analysis_queue']?.length > 0 ||
        Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.length > 0);
    assert('caseId' in caseInfo, 'caseId not found in caseInfo');

    // Helper function to get currency code for a medical expense item
    const getCurrencyCode = (documentKey, itemIndex) => {
        try {
            const currencyPath = `$.documents['${documentKey}'].analysis.medical_expenses[${itemIndex}].currency_code`;
            const results = JSONPath({
                json: caseInfo,
                path: currencyPath,
                resultType: 'value'
            });
            if (results.length === 0) {
                return 'USD';
            }
            const currencyCode = results[0];

            // List of valid ISO 4217 currency codes
            const validCurrencyCodes = [
                'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
                'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD',
                'CAD', 'CDF', 'CHF', 'CLP', 'CNY', 'COP', 'CRC', 'CUP', 'CVE', 'CZK',
                'DJF', 'DKK', 'DOP', 'DZD',
                'EGP', 'ERN', 'ETB', 'EUR',
                'FJD', 'FKP',
                'GBP', 'GEL', 'GGP', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD',
                'HKD', 'HNL', 'HRK', 'HTG', 'HUF',
                'IDR', 'ILS', 'IMP', 'INR', 'IQD', 'IRR', 'ISK',
                'JEP', 'JMD', 'JOD', 'JPY',
                'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
                'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD',
                'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN',
                'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD',
                'OMR',
                'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG',
                'QAR',
                'RON', 'RSD', 'RUB', 'RWF',
                'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SOS', 'SRD', 'STN', 'SYP', 'SZL',
                'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TVD', 'TWD', 'TZS',
                'UAH', 'UGX', 'USD', 'UYU', 'UZS',
                'VED', 'VES', 'VND', 'VUV',
                'WST',
                'XAF', 'XCD', 'XDR', 'XOF', 'XPF',
                'YER',
                'ZAR', 'ZMW', 'ZWL'
            ];

            // Check if the currency code is in the valid list
            if (!validCurrencyCodes.includes(currencyCode)) {
                console.warn(`Invalid currency code '${currencyCode}' found, falling back to USD`);
                return 'USD';
            }
            return currencyCode;
        } catch (error) {
            console.error('Error extracting currency code:', error);
            return 'USD';
        }
    };


    // Auto-switch to consolidated view when consolidation completes
    useEffect(() => {
        if (hasConsolidated) {
            setShowConsolidatedOnly(true);
        }
    }, [hasConsolidated]);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header section with ButtonGroup on left and CopyToClipboardButton on right */}
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {hasConsolidated && (
                        <ButtonGroup
                            sx={{
                                visibility: hasConsolidated ? "visible" : "hidden",
                                pointerEvents: "auto"
                            }}
                            aria-label="view mode button group"
                            variant="text"
                            size="small"
                            className="selectConsolidatedButtonGroup"
                        >
                            <Button
                                onClick={() => setShowConsolidatedOnly(false)}
                                disabled={!showConsolidatedOnly}
                                key="all"
                                size="small"
                            >
                                All Records
                            </Button>
                            <Button
                                onClick={() => setShowConsolidatedOnly(true)}
                                disabled={showConsolidatedOnly}
                                key="consolidated"
                                size="small"
                            >
                                Consolidated Records
                            </Button>
                        </ButtonGroup>
                    )}
                    {isMedicalRecordsInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle",
                                    opacity: 0.7
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Medical Expenses are being extracted...
                            </Typography>
                        </Box>
                    )}
                    {!isMedicalRecordsInProgress && isConsolidationInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle",
                                    opacity: 0.7
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Consolidating medical expenses...
                            </Typography>
                        </Box>
                    )}
                    {((hasConsolidated && hasUnconsolidated) || (unconsolidatedFiles.length > 1)) && !isProcessingInProgress && (
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)'
                        }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--info-color)',
                                }}
                            >
                                {hasConsolidated && hasUnconsolidated
                                    ? `Click Consolidate to incorporate new data extracted from ${unconsolidatedFiles.length} files`
                                    : 'Raw information extracted from files below. Click Consolidate to generate a single, master medical report.'
                                }
                            </Typography>
                            <Fab
                                onClick={async () => {
                                    const response = await fetch('/api/case/' + caseInfo['caseId'] + '/analysis_queue', {
                                        method: 'POST',
                                        credentials: 'same-origin',
                                        headers: new Headers({ 'content-type': 'application/json' }),
                                        body: JSON.stringify({
                                            analysis_queue: ['medical_reports_canonicalize'],
                                            item_type: 'consolidation'
                                        })
                                    });
                                    console.log('Consolidate button clicked: ' + response);

                                    if (response.ok) {
                                        setCaseInfo({ ...caseInfo, analysis_queue: [...caseInfo['analysis_queue'] || [], 'medical_reports_canonicalize'] });
                                        setTimeout(fetchCaseInfoAndUpdateUI, 10000);
                                    }
                                }}
                                variant="extended"
                                size="medium"
                                color="primary"
                                sx={{
                                    top: "-1px",
                                    background: 'var(--button-gradient)',
                                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.95rem',
                                    padding: '14px 24px',
                                    borderRadius: '12px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: 'none',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                                        background: 'var(--button-gradient-hover)'
                                    },
                                    '&:active': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        transition: 'left 0.6s'
                                    },
                                    '&:hover::before': {
                                        left: '100%'
                                    }
                                }}
                            >
                                <MergeIcon sx={{ mr: 1 }} style={{ transform: 'rotate(90deg)' }} />
                                Consolidate
                            </Fab>
                        </Box>
                    )}
                </Box>
                {!showConsolidatedOnly && <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Expenses" />}
            </Box>

            {/* Content section below the header */}
            {showConsolidatedOnly ? consolidatedMedicalExpenses() : allMedicalExpenses()}
        </Box>
    );

    function consolidatedMedicalExpenses() {
        const medicalReports = caseInfo?.analysis?.medical_reports || [];

        if (medicalReports.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        No consolidated medical reports available.
                    </Typography>
                </Box>
            );
        }

        return (
            <Box>
                {medicalReports.map((report, reportIndex) => (
                    <Accordion
                        key={report.uuid || reportIndex}
                        defaultExpanded={true}
                        sx={{
                            backgroundColor: 'var(--background-color)',
                            border: '1px solid var(--border-color)',
                            boxShadow: 'var(--shadow-light)',
                            mb: 2,
                            '&:before': {
                                display: 'none'
                            }
                        }}
                    >
                        <AccordionSummary
                            // Make only icon clickable to expand/contract Accordian from https://stackoverflow.com/questions/69358781/material-ui-expand-accordion-by-clicking-the-icon-only
                            sx={{
                                pointerEvents: "none",
                                backgroundColor: 'var(--surface-color)',
                                borderBottom: '1px solid var(--border-color)',
                                height: '64px', // Without this, height of accordian summary changes when expanded or collapsed due to minHeigh rule only applied on expanded by library.
                                padding: '8px 16px',
                                '&:hover': {
                                    backgroundColor: 'var(--surface-color)'
                                }
                            }}
                            expandIcon={<ExpandMoreIcon
                                sx={{
                                    pointerEvents: "auto",
                                    color: 'var(--text-secondary)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        color: 'var(--accent-color)',
                                        transform: 'scale(1.1)',
                                    }
                                }}
                            // onClick={onAccordianIconClicked}
                            />}
                            style={{
                                flexDirection: "row-reverse",
                                cursor: "default",
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: 'var(--text-primary)'
                                }}
                            >
                                Medical Expenses for {report.patient_name || 'Unknown Patient'}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3 }}>
                            {/* Summary Table */}
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                    Summary
                                </Typography>
                                <CopyToClipboardButton nodeRef={summaryTableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Expenses Summary" />
                            </Box>
                            {renderSummaryTable(report, reportIndex)}

                            {/* Medical Expenses Table */}
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2
                            }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                    Medical Expenses
                                </Typography>
                                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Medical Expenses" />
                            </Box>
                            {renderConsolidatedMedicalExpensesTable(report.medical_expenses || [], reportIndex)}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    }

    function allMedicalExpenses() {
        // Get document-based medical expenses (All Records)
        var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
            Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap[caseInfo['documents'][docKey]['filename']]['selected']) :
            [];
        var medicalExpenses = documentKeysToDisplay.map(documentKey => {
            caseInfo['documents'][documentKey]['analysis']?.['medical_expenses']?.forEach((medicalExpense, i) => {
                medicalExpense['id'] = documentKey + i; // Used in UI as keys for use by React framework
                medicalExpense['source_filekey'] = documentKey; // File used by SourceDocumentViewer
            });
            return caseInfo['documents'][documentKey]['analysis']?.['medical_expenses'];
        })
            .filter(medicalExpenseArr => medicalExpenseArr) // the ?. above can cause null entries; filter them
            .reduce((accumulator, medicalExpenseArr) => accumulator.concat(medicalExpenseArr), [])
            .filter(medicalExpense => isValidMedicalExpenseItem(medicalExpense)) // Remove '', 'N/A', null, etc
            .sort((expense1, expense2) =>
                Date.parse(expense1.treatment_start_date || expense1.treatment_date || '') -
                Date.parse(expense2.treatment_start_date || expense2.treatment_date || ''));

        if (medicalExpenses.length === 0) {
            return (
                <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    mt: 8,
                    mb: 8
                }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontWeight: 400,
                            fontSize: '1.1rem',
                            lineHeight: 1.5
                        }}
                    >
                        Medical charges will be automatically extracted from uploaded medical bills, invoices, etc and listed here.
                    </Typography>
                </Box>
            )
        }

        var tableBodyRows = medicalExpenses
            .map(medicalExpense => {
                // Find the document key for this expense item
                const documentKey = medicalExpense.source_filekey;
                const docInfo = caseInfo.documents[documentKey];
                const filename = docInfo?.['filename'];
                const itemIndex = caseInfo.documents[documentKey]?.analysis?.medical_expenses?.findIndex(item => item.id === medicalExpense.id) || 0;

                return (
                    <TableRow
                        key={medicalExpense['id']}
                        className={medicalExpense['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}
                    >
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            {renderTreatmentDate(medicalExpense, documentKey, itemIndex)}
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'patient_name')}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ fontWeight: 500 }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '200px'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'treatment')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            maxWidth: '150px'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'payee')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense['source']?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'insurance')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            fontWeight: 500,
                            textAlign: 'right'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'total_charge')}
                                formatValue={getFormatFunction('total_charge')}
                                parseValue={getParseFunction('total_charge')}
                                callbackArguments={{ currencyCode: getCurrencyCode(documentKey, itemIndex), locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                            textAlign: 'right'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'total_adjustment')}
                                formatValue={getFormatFunction('total_adjustment')}
                                parseValue={getParseFunction('total_adjustment')}
                                callbackArguments={{ currencyCode: getCurrencyCode(documentKey, itemIndex), locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            fontWeight: 500,
                            textAlign: 'right'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'total_balance')}
                                formatValue={getFormatFunction('total_balance')}
                                parseValue={getParseFunction('total_balance')}
                                callbackArguments={{ currencyCode: getCurrencyCode(documentKey, itemIndex), locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            fontWeight: 500,
                            textAlign: 'right'
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': medicalExpense.source?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(documentKey, 'medical_expenses', itemIndex, 'total_paid')}
                                formatValue={getFormatFunction('total_paid')}
                                parseValue={getParseFunction('total_paid')}
                                callbackArguments={{ currencyCode: getCurrencyCode(documentKey, itemIndex), locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '0.9rem',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': medicalExpense['source_filekey'],
                                            'page': medicalExpense.source?.page || 1
                                        });
                                    }}
                                >
                                    {filename} {(medicalExpense.source?.page || medicalExpense.page) && `(page ${medicalExpense.source?.page || medicalExpense.page})`}{medicalExpense.source?.bates_number && `(${medicalExpense.source?.bates_number})`}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': medicalExpense['source_filekey'],
                                            'page': medicalExpense.source?.page || 1
                                        });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            });

        return (
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="medical expenses table"
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "150px"
                        }}>
                            Treatment Date
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Patient Name
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Treatment/Service
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Provider/Hospital
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Insurance
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Total Charge
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Adjustments/Payments
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Total Balance
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Total Paid
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        );
    }

    // Helper functions for consolidated medical expenses (new array schema for needs_review/caveats)
    function buildConsolidatedFieldPaths(reportIndex, dataType, itemIndex, fieldName) {
        const base = `$.analysis.medical_reports[${reportIndex}].${dataType}[${itemIndex}]`;
        return {
            textPath: `${base}.${fieldName}`,
            needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
            caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
        }
    }

    function buildConsolidatedSingleFieldPaths(reportIndex, dataType, fieldName) {
        const base = `$.analysis.medical_reports[${reportIndex}].${dataType}`;
        return {
            textPath: `${base}.${fieldName}`,
            needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
            caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
        }
    }

    function renderSummaryTable(report, reportIndex) {
        const summary = report.medical_expenses_summary || {};
        // Get the first source from the first medical expense for warning navigation
        const firstSource = report.medical_expenses?.[0]?.sources?.[0] ||
            report.medical_expenses?.[0]?.source || {};

        return (
            <Table
                ref={summaryTableRef}
                sx={{
                    mb: 4,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            width: '200px'
                        }}>
                            Description
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Details
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px'
                        }}>
                            Total Charge
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: 500
                        }}>
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildConsolidatedSingleFieldPaths(reportIndex, 'medical_expenses_summary', 'total_charge')}
                                formatValue={getFormatFunction('total_charge')}
                                parseValue={getParseFunction('total_charge')}
                                callbackArguments={{ currencyCode: summary.currency_code || 'USD', locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', fontWeight: 500 }}
                                onWarningClick={() => {
                                    setDisplayedDocument({
                                        'key': getJSONPathCompatibleKey(firstSource?.filename || ''),
                                        'page': firstSource?.page || 1
                                    });
                                }}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px'
                        }}>
                            Adjustments/Payments
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px',
                            textAlign: 'right'
                        }}>
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildConsolidatedSingleFieldPaths(reportIndex, 'medical_expenses_summary', 'total_adjustment')}
                                formatValue={getFormatFunction('total_adjustment')}
                                parseValue={getParseFunction('total_adjustment')}
                                callbackArguments={{ currencyCode: summary.currency_code || 'USD', locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right' }}
                                onWarningClick={() => {
                                    setDisplayedDocument({
                                        'key': getJSONPathCompatibleKey(firstSource?.filename || ''),
                                        'page': firstSource?.page || 1
                                    });
                                }}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px'
                        }}>
                            Total Balance
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: 500
                        }}>
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildConsolidatedSingleFieldPaths(reportIndex, 'medical_expenses_summary', 'total_balance')}
                                formatValue={getFormatFunction('total_balance')}
                                parseValue={getParseFunction('total_balance')}
                                callbackArguments={{ currencyCode: summary.currency_code || 'USD', locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', fontWeight: 500 }}
                                onWarningClick={() => {
                                    setDisplayedDocument({
                                        'key': getJSONPathCompatibleKey(firstSource?.filename || ''),
                                        'page': firstSource?.page || 1
                                    });
                                }}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px'
                        }}>
                            Total Paid
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                            padding: '12px',
                            textAlign: 'right',
                            fontWeight: 500
                        }}>
                            <EditableFieldWithWarnings
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildConsolidatedSingleFieldPaths(reportIndex, 'medical_expenses_summary', 'total_paid')}
                                formatValue={getFormatFunction('total_paid')}
                                parseValue={getParseFunction('total_paid')}
                                callbackArguments={{ currencyCode: summary.currency_code || 'USD', locale: getUserLocale() }}
                                setSnackBarMessage={setSnackBarMessage}
                                style={{ textAlign: 'right', fontWeight: 500 }}
                                onWarningClick={() => {
                                    setDisplayedDocument({
                                        'key': getJSONPathCompatibleKey(firstSource?.filename || ''),
                                        'page': firstSource?.page || 1
                                    });
                                }}
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        );
    }

    function renderConsolidatedMedicalExpensesTable(medicalExpenses, reportIndex) {
        if (!medicalExpenses || medicalExpenses.length === 0) {
            return (
                <Typography sx={{ color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                    No medical expenses available
                </Typography>
            );
        }

        // Sort medical expenses by treatment date
        const sortedExpenses = [...medicalExpenses].sort((expense1, expense2) =>
            Date.parse(expense1.treatment_start_date || expense1.treatment_date || '') -
            Date.parse(expense2.treatment_start_date || expense2.treatment_date || '')
        );

        const tableBodyRows = sortedExpenses.map((medicalExpense, itemIndex) => {
            let source = medicalExpense.source || medicalExpense.sources?.[0] || {};
            return (
                <TableRow
                    key={medicalExpense.uuid || itemIndex}
                    sx={{
                        '&:hover .source-view-icon': {
                            visibility: 'visible',
                            opacity: 0.7
                        }
                    }}
                >
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderConsolidatedTreatmentDate(medicalExpense, reportIndex, itemIndex)}
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'patient_name')}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ fontWeight: 500 }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        maxWidth: '200px'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'treatment')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source?.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        maxWidth: '150px'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'payee')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source?.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'insurance')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source?.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 500,
                        textAlign: 'right'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'total_charge')}
                            formatValue={getFormatFunction('total_charge')}
                            parseValue={getParseFunction('total_charge')}
                            callbackArguments={{ currencyCode: medicalExpense.currency_code || 'USD', locale: getUserLocale() }}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source?.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                        textAlign: 'right'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'total_adjustment')}
                            formatValue={getFormatFunction('total_adjustment')}
                            parseValue={getParseFunction('total_adjustment')}
                            callbackArguments={{ currencyCode: medicalExpense.currency_code || 'USD', locale: getUserLocale() }}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source?.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 500,
                        textAlign: 'right'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'total_balance')}
                            formatValue={getFormatFunction('total_balance')}
                            parseValue={getParseFunction('total_balance')}
                            callbackArguments={{ currencyCode: medicalExpense.currency_code || 'USD', locale: getUserLocale() }}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source?.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 500,
                        textAlign: 'right'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildConsolidatedFieldPaths(reportIndex, 'medical_expenses', itemIndex, 'total_paid')}
                            formatValue={getFormatFunction('total_paid')}
                            parseValue={getParseFunction('total_paid')}
                            callbackArguments={{ currencyCode: medicalExpense.currency_code || 'USD', locale: getUserLocale() }}
                            setSnackBarMessage={setSnackBarMessage}
                            style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}
                            onWarningClick={() => {
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename || ''),
                                    'page': source?.page || 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-secondary)',
                        borderBottom: '1px solid var(--border-color)',
                    }}>
                        {renderSourcesList(medicalExpense.sources || [])}
                    </TableCell>
                </TableRow>
            )
        });

        return (
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="consolidated medical expenses table"
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "150px"
                        }}>
                            Treatment Date
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Patient Name
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Treatment/Service
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Provider/Hospital
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Insurance
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Total Charge
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Adjustments/Payments
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Total Balance
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            textAlign: 'right'
                        }}>
                            Total Paid
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        );
    }

    function renderConsolidatedTreatmentDate(medicalExpense, reportIndex, itemIndex) {
        const baseFieldPath = `$.analysis.medical_reports[${reportIndex}].medical_expenses[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument);
    }

    function renderSourcesList(sources) {
        if (!sources || sources.length === 0) {
            return 'N/A';
        }
        return (
            <Box>
                {sources.map((source, index) => (
                    <Box key={index} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            component="span"
                            title="Click to view source document"
                            sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: '0.9rem',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename),
                                    'page': source?.page
                                });
                            }}
                        >
                            {source?.filename} {source?.page && `(page ${source?.page})`}{source?.bates_number && `(${source?.bates_number})`}
                        </Typography>
                        <IconButton
                            className="source-view-icon"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': getJSONPathCompatibleKey(source?.filename),
                                    'page': source?.page
                                });
                            }}
                            sx={{
                                padding: '2px',
                                visibility: 'hidden',
                                opacity: 0,
                                transition: 'opacity 0.2s, visibility 0.2s',
                                '&:hover': {
                                    opacity: 1,
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                            title="Click to view source document"
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        );
    }

    function isValidMedicalExpenseItem(medicalExpense) {
        return medicalExpense['total_charge'];
    }

    function renderTreatmentDate(medicalExpense, documentKey, itemIndex) {
        const baseFieldPath = `$.documents['${documentKey}'].analysis.medical_expenses[${itemIndex}]`;
        return renderTreatmentDates(caseInfo, setCaseInfo, baseFieldPath, setSnackBarMessage, setDisplayedDocument, documentKey);
    }
}

// TODO: Move to helper
function assert(condition, message) {
    if (!condition) {
        throw new Error(`Invariant failed: ${message || "Condition not met."}`);
    }
}
